import { format, isValid, parse } from "date-fns";

/** How license dates read on screen; `parseLicenseDate` reads the same text back. */
const LICENSE_DATE_PATTERN = "dd-MM-yyyy";

/**
 * License start, end and device activation are Sequelize `DATE` columns, so
 * they arrive as UTC instants ("2024-05-27T00:00:00.000Z") and the day they
 * name is the UTC one. Read in local time, midnight UTC is the previous evening
 * anywhere west of UTC, and the page showed one day less than the old
 * dashboard, which reads the UTC fields (CTX-1425). The UTC day is copied onto
 * a local date so date-fns keeps owning the pattern. An unparseable value is
 * shown as-is instead of throwing and emptying the whole table.
 */
export function formatLicenseDate(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const utcDay = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );
  return format(utcDay, LICENSE_DATE_PATTERN);
}

/**
 * Reads back a date `formatLicenseDate` rendered, as a local date on that same
 * calendar day — what the date picker edits. The Admin Center's Edit License is
 * prefilled from the table's text, and the modal used to try `new Date(text)`
 * first, which reads "09-02-2025" month-first as 2 September (CTX-1438).
 * Anything not in the pattern, an ISO instant included, is `null`.
 */
export function parseLicenseDate(text: string | undefined): Date | null {
  if (!text) return null;
  const day = parse(text, LICENSE_DATE_PATTERN, new Date());
  return isValid(day) ? day : null;
}
